/**
 * Re-export palette.json as a typed module.
 * Fallback inline values allow the app to work even if the JSON fetch fails.
 */

export interface PaletteColors {
  [key: string]: { hex: string; name: string; usage: string[] };
}

export interface PaletteMaterial {
  base: string;
  roughness: number;
  metallic: number;
}

export interface Palette {
  version: string;
  name: string;
  description: string;
  colors: PaletteColors;
  materials: Record<string, PaletteMaterial>;
}

// Inline fallback — matches shared/palette.json
const fallback: Palette = {
  version: '1.0',
  name: 'Constructo Arcade Palette',
  description: 'DRY color/material source across client/server/asset pipeline.',
  colors: {
    concrete:       { hex: '#E8DCC8', name: 'Warm Concrete',     usage: ['barriers', 'buildings', 'ground slabs'] },
    steel:          { hex: '#A0B0C0', name: 'Cool Steel',        usage: ['vehicles', 'girders', 'tools'] },
    wood:           { hex: '#D4A056', name: 'Golden Wood',       usage: ['crates', 'scaffolding', 'pallets'] },
    foliage:        { hex: '#4CE05A', name: 'Vibrant Foliage',   usage: ['tree canopies', 'bushes'] },
    trunk:          { hex: '#8B5E3C', name: 'Tree Trunk',        usage: ['tree trunks', 'logs'] },
    portaloo_blue:  { hex: '#4DA6FF', name: 'Portaloo Blue',     usage: ['portaloos'] },
    portaloo_white: { hex: '#F5F0E8', name: 'Portaloo White',    usage: ['portaloo trim'] },
    warning_orange: { hex: '#FF8800', name: 'Warning Orange',    usage: ['cones', 'hazard stripes', 'UI warnings'] },
    danger_red:     { hex: '#FF3344', name: 'Danger Red',        usage: ['explosives', 'UI errors', 'destruction fx'] },
    player_blue:    { hex: '#3388FF', name: 'Player Blue',       usage: ['player indicators', 'friendly markers'] },
    grass:          { hex: '#55DD55', name: 'Arcade Grass',      usage: ['terrain', 'ground plane'] },
    sky:            { hex: '#88CCFF', name: 'Arcade Sky',        usage: ['skybox', 'fog color'] },
    dirt:           { hex: '#C8963C', name: 'Arcade Dirt',       usage: ['excavated terrain', 'soil heaps'] },
    water:          { hex: '#3399FF', name: 'Arcade Water',      usage: ['water plane', 'puddles'] },
    hi_vis_yellow:  { hex: '#FFC61A', name: 'Hi-Vis Yellow',  usage: ['excavator/dozer bodies', 'crane accents'] },
    cab_glass:      { hex: '#1E2A38', name: 'Cab Glass',      usage: ['vehicle windows'] },
    tyre_dark:      { hex: '#2A2A2E', name: 'Tyre Dark',      usage: ['wheels', 'tracks'] },
    vest_orange:    { hex: '#FF6A00', name: 'Hi-Vis Vest',    usage: ['character vest'] },
    denim_blue:     { hex: '#2E4A7A', name: 'Work Denim',     usage: ['character trousers'] },
    skin_tone:      { hex: '#E8B08A', name: 'Skin',           usage: ['character face/hands'] },
    helmet_white:   { hex: '#F2F2F2', name: 'Hard Hat',       usage: ['character helmet'] },
    road_dark:      { hex: '#3A3F45', name: 'Asphalt',        usage: ['roads', 'spawn pad trim'] },
    rock_grey:      { hex: '#8D8D94', name: 'Rock',           usage: ['rocks', 'rubble'] },
  },
  materials: {
    concrete:       { base: 'concrete',        roughness: 0.70, metallic: 0.00 },
    steel:          { base: 'steel',           roughness: 0.30, metallic: 0.80 },
    wood:           { base: 'wood',            roughness: 0.80, metallic: 0.00 },
    foliage:        { base: 'foliage',         roughness: 0.90, metallic: 0.00 },
    trunk:          { base: 'trunk',           roughness: 0.85, metallic: 0.00 },
    portaloo:       { base: 'portaloo_blue',   roughness: 0.35, metallic: 0.05 },
    portaloo_trim:  { base: 'portaloo_white',  roughness: 0.40, metallic: 0.05 },
    barrier_plastic: { base: 'warning_orange', roughness: 0.50, metallic: 0.00 },
    danger_red:     { base: 'danger_red',       roughness: 0.50, metallic: 0.00 },
    hi_vis_yellow: { base: 'hi_vis_yellow', roughness: 0.45, metallic: 0.10 },
    cab_glass:     { base: 'cab_glass',      roughness: 0.15, metallic: 0.60 },
    tyre_dark:     { base: 'tyre_dark',      roughness: 0.95, metallic: 0.00 },
    vest_orange:   { base: 'vest_orange',    roughness: 0.60, metallic: 0.00 },
    denim_blue:    { base: 'denim_blue',     roughness: 0.80, metallic: 0.00 },
    skin_tone:     { base: 'skin_tone',      roughness: 0.60, metallic: 0.00 },
    helmet_white:  { base: 'helmet_white',   roughness: 0.30, metallic: 0.05 },
    road_dark:     { base: 'road_dark',      roughness: 0.95, metallic: 0.00 },
    rock_grey:     { base: 'rock_grey',      roughness: 0.90, metallic: 0.00 },
  },
};

// Try to fetch palette.json at startup; fall back to inline values.
// Export a promise so consumers can await.
let _palette: Palette = fallback;

export async function loadPalette(): Promise<Palette> {
  try {
    const res = await fetch('/assets/palette.json');
    if (res.ok) {
      _palette = await res.json();
    }
  } catch {
    // Use fallback
  }
  return _palette;
}

/** Synchronous accessor (call after loadPalette resolves). */
export function getPalette(): Palette {
  return _palette;
}

// Default export for convenience (always returns loaded or fallback).
const pal: Palette = _palette;
export default pal;